import React, { useState } from 'react';
import TimelineCalendar, {
	TimelineCalendarEvent,
} from '../TimelineCalendar/TimelineCalendar';
import { ServiceScheduleType } from '../../../../graphql/graphql';
import useServiceSchedules from '../../../Workplace/Hooks/useServiceSchedules';
import { getMomentDate } from '../../../../Utils/dates.util';
import { useEffectAsync } from '../../../../helpers';
import OverlayLoading from '../../../../Components/OverlayLoading';
import ContextMenu from '../../../../Components/ContextMenu/ContextMenu';
import ServiceScheduleCreateModal from '../../../ServiceSchedule/Components/ServiceScheduleCreate/ServiceScheduleCreateModal';
import { useServiceScheduleContext } from '../../../WorkplaceMember/Services/ServiceScheduleContext';
import ServiceScheduleUpdateModal from '../../../ServiceSchedule/Components/ServiceScheduleUpdate/ServiceScheduleUpdateModal';
import ConfirmationModal from '../../../Base/Components/Confirmation/ConfirmationModal';
import Button from '../../../../Components/Button/Button';
import { Theme } from '../../../../Enums/Theme.enum';
import useTranslation from '../../../../Hooks/useTranslation';
import LinkButton from '../../../../Components/Button/LinkButton';
import { getShouldBeHintDisplayed } from '../../../Manager/Services/manager.service';
import ServiceScheduleInstructionsModal from '../../../Manager/Components/ServiceSchedules/ServiceScheduleInstructions/ServiceScheduleInstructionsModal';
import Loading from '../../../../Components/Loading';
import useApiClient from '../../../Base/Hooks/useApiClient';
import useRestApiClient from '../../../Base/Hooks/useRestApiClient';
import useCalendarWithSchedules from '../../../ServiceSchedule/Hooks/useCalendarWithSchedules';
import { CustomSettingKey } from '../../../Base/Enums/CustomSettingKey.enum';
import { calendarPrint } from '../../Services/calendarPrint.service';
import { CalendarPrintType } from '../../Enums/CalendarPrintType.enum';

interface Props {
	scheduleTypes: ServiceScheduleType[];
	workplaceId: string;
	navigateToOnlineOrdering: () => void;
}

const ServiceScheduleCalendar: React.FC<Props> = ({
	workplaceId,
	scheduleTypes,
	navigateToOnlineOrdering,
}) => {
	const {
		events,
		setEvents,
		dateRange,
		currentCalendarView,
		isCreateModalOpen,
		setIsCreateModalOpen,
		isEditModalOpen,
		setIsEditModalOpen,
		contextMenuProps,
		setContextMenuProps,
		selectedDate,
		selectedResourceId,
		selectedServiceSchedule,
		selectedServiceScheduleItem,
		isDeleteConfirmationOpen,
		setIsDeleteConfirmationOpen,
		isMultipleDeleteConfirmationOpen,
		setIsMultipleDeleteConfirmationOpen,
		isInstructionModalOpen,
		setIsInstructionModalOpen,
		isLoading,
		handleDateRangeChange,
		handleChangeView,
		handleDateClick,
		handleEventClick,
		handleWipeStates,
		handleCloseCreateModal,
		handleCloseUpdateModal,
		handleOpenRemoveSingle,
		handleOpenRemoveMultiple,
		handleDeleteServiceSchedule,
		handleRemove,
		handleRenderEvent,
	} = useCalendarWithSchedules(workplaceId);

	const { timelineCalendarResources, workplaceName } =
		useServiceScheduleContext();
	const { t } = useTranslation();
	const {
		getSchedulesAsTimeline,
		getAbsenceEventsAsTimeline,
		isLoading: isLoadingEvents,
	} = useServiceSchedules();
	const [isLoadingPrint, setIsLoadingPrint] = useState(false);
	const { displayError } = useApiClient();
	const { showUnexpectedError } = useRestApiClient();

	const handleLoadSchedule = async () => {
		const tempEvents: TimelineCalendarEvent[] = [];
		if (dateRange) {
			const shiftEvents = await getSchedulesAsTimeline({
				from: getMomentDate(dateRange.start),
				to: getMomentDate(dateRange.end),
				workplaceIds: [workplaceId],
				types: scheduleTypes,
				calendarView: currentCalendarView,
			});
			tempEvents.push(...shiftEvents);
			const absenceEvents = await getAbsenceEventsAsTimeline({
				from: getMomentDate(dateRange.start),
				to: getMomentDate(dateRange.end),
				workplaceIds: [workplaceId],
				calendarView: currentCalendarView,
			});
			tempEvents.push(...absenceEvents);
		}
		setEvents(tempEvents);
	};

	useEffectAsync(async () => {
		await handleLoadSchedule();
	}, [dateRange, currentCalendarView]);

	const handleOnSuccess = async () => {
		handleWipeStates();
		await handleLoadSchedule();
	};

	const handlePrint = async () => {
		if (!dateRange) return;
		setIsLoadingPrint(true);
		try {
			await calendarPrint({
				type: CalendarPrintType.SHIFT_REPORT,
				dateFrom: dateRange.start,
				dateTo: dateRange.end,
				workplaceId,
			});
		} catch (e) {
			displayError(e);
		}
		setIsLoadingPrint(false);
	};

	return (
		<div style={{ height: '100%', position: 'relative' }}>
			{isLoadingEvents || (isLoadingPrint && <OverlayLoading />)}
			<TimelineCalendar
				resources={timelineCalendarResources}
				events={events}
				onDateRangeChange={handleDateRangeChange}
				onDateClick={handleDateClick}
				onEventClick={handleEventClick}
				onViewChange={handleChangeView}
				resourcesLabel={workplaceName}
				renderEvent={handleRenderEvent}
				onPrint={handlePrint}
				resourceGroupField='groupKey'
				customSettingKey={CustomSettingKey.SHIFTS_CALENDAR_SETTINGS}
				workplaceId={workplaceId}
			/>
			{isCreateModalOpen && selectedDate && selectedResourceId && (
				<ServiceScheduleCreateModal
					isOpen
					onClose={handleCloseCreateModal}
					selectedDate={selectedDate}
					resourceId={selectedResourceId}
					onSuccess={handleOnSuccess}
				/>
			)}
			{isEditModalOpen &&
				selectedDate &&
				selectedServiceSchedule &&
				selectedServiceScheduleItem && (
					<ServiceScheduleUpdateModal
						isOpen
						selectedDate={selectedDate}
						serviceSchedule={selectedServiceSchedule}
						serviceScheduleItem={selectedServiceScheduleItem}
						onSuccess={handleOnSuccess}
						onClose={handleCloseUpdateModal}
						onDelete={handleDeleteServiceSchedule}
					/>
				)}
			{isMultipleDeleteConfirmationOpen && (
				<ConfirmationModal
					title='base.deleteSchedule'
					text={t(
						'component.schedule.deleteMultipleScheduleConfirmationMessage',
					)}
					isOpen
					onClose={() => setIsMultipleDeleteConfirmationOpen(false)}>
					<div className='d-flex flex-row' style={{ gap: '15px' }}>
						{isLoading ? (
							<Loading />
						) : (
							<>
								<Button
									onClick={() => handleRemove(false, handleLoadSchedule)}
									title={'base.deleteOnlyThisOne'}
									theme={Theme.GREEN}
									disabled={isLoading}
								/>
								<Button
									onClick={() => handleRemove(true, handleLoadSchedule)}
									title={'base.deleleAlsoFutures'}
									theme={Theme.BLUE}
									disabled={isLoading}
								/>
							</>
						)}
					</div>
				</ConfirmationModal>
			)}
			{isDeleteConfirmationOpen && (
				<ConfirmationModal
					title='base.deleteSchedule'
					text={t('component.schedule.deleteScheduleConfirmationMessage') ?? ''}
					isOpen={isDeleteConfirmationOpen}
					onClose={() => setIsDeleteConfirmationOpen(false)}>
					{isLoading ? (
						<Loading />
					) : (
						<Button
							onClick={() => handleRemove(false, handleLoadSchedule)}
							title={'base.shared.delete'}
							theme={Theme.GREEN}
						/>
					)}
				</ConfirmationModal>
			)}
			{contextMenuProps.isOpen &&
				selectedDate &&
				selectedServiceSchedule &&
				selectedServiceScheduleItem && (
					<ContextMenu
						params={contextMenuProps}
						onContextClose={() => setContextMenuProps({ isOpen: false })}>
						<LinkButton
							onClick={() => {
								setIsEditModalOpen(true);
								setContextMenuProps({ isOpen: false });
							}}
							title={'base.shared.edit'}
							theme={Theme.BLUE}
							firstUpper
						/>
						<LinkButton
							onClick={() => {
								setIsCreateModalOpen(true);
								setContextMenuProps({ isOpen: false });
							}}
							title={'component.workplaceScheduleCalendar.createNewSchedule'}
							theme={Theme.BLUE}
							firstUpper
						/>
						<div style={{ marginTop: '20px' }} />
						{selectedServiceScheduleItem.repeatable ? (
							<LinkButton
								theme={Theme.RED}
								title='base.deleteSchedule'
								onClick={handleOpenRemoveMultiple}
								firstUpper
							/>
						) : (
							<LinkButton
								theme={Theme.RED}
								title='base.deleteSchedule'
								onClick={handleOpenRemoveSingle}
								firstUpper
							/>
						)}
					</ContextMenu>
				)}
			{isInstructionModalOpen &&
				getShouldBeHintDisplayed(
					'doNoShowServiceScheduleInstructionsModal',
				) && (
					<ServiceScheduleInstructionsModal
						isOpened
						onClose={() => setIsInstructionModalOpen(false)}
					/>
				)}
		</div>
	);
};

export default ServiceScheduleCalendar;
